import React from 'react';
import * as styles from './ReorderSidebarItemsModal.module.scss';
import Card, { Cards } from '../components/Card';
import Form, { FieldArray, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import ReorderableList from '../components/ReorderableList';
import SelectEntityOption from '../components/SelectEntityOption';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import nonEmpty from '../utils/nonEmpty';

const TOPIC_FRAGMENT = gql(`
  fragment ReorderSidebarItemsModal_topic on Topic {
    id
    name
  }
`);

const POST_FRAGMENT = gql(`
  fragment ReorderSidebarItemsModal_post on Post {
    id
    title
  }
`);

type ReorderableSidebarItem =
  | DocumentType<typeof TOPIC_FRAGMENT>
  | DocumentType<typeof POST_FRAGMENT>;

interface ReorderSidebarItemsModalProps {
  onSubmit: (topicIds: string[]) => void;
  headerIcon?: React.ReactNode;
  header: string;
  headerSecondaryTop?: React.ReactNode;
  title: string;
  subtitle: string;
  items: ReorderableSidebarItem[];
  close: () => void;
}

const ReorderSidebarItemsModal = ({
  onSubmit,
  headerIcon,
  header,
  headerSecondaryTop,
  title,
  subtitle,
  items,
  close,
}: ReorderSidebarItemsModalProps) => {
  return (
    <Modal>
      <ModalTitle close={close} subtitle={subtitle}>
        {title}
      </ModalTitle>
      <Form<{ items: ReorderableSidebarItem[] }>
        onSubmit={(values) =>
          onSubmit(values.items.map((item) => item.id).filter(nonEmpty))
        }
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ items }}
      >
        {({ values }) => (
          <>
            <FieldArray name="items">
              {(arrayHelpers) => (
                <Cards>
                  <Card noPadding>
                    <div className={styles.header}>
                      <SelectEntityOption
                        icon={headerIcon}
                        secondaryTop={headerSecondaryTop}
                        label={header}
                      />
                    </div>
                  </Card>
                  <ReorderableList<ReorderableSidebarItem>
                    name="items"
                    onMove={arrayHelpers.move}
                    items={values.items}
                    renderItem={(
                      item: ReorderableSidebarItem,
                      index: number,
                    ) => {
                      const itemName = (() => {
                        if (item.__typename === 'Post') {
                          return item.title;
                        }

                        if (item.__typename === 'Topic') {
                          return item.name;
                        }

                        return '';
                      })();

                      return (
                        <div className={styles.item}>
                          <div className={styles.itemIcon}>{index + 1}</div>
                          <div className={styles.itemName}>{itemName}</div>
                        </div>
                      );
                    }}
                  />
                </Cards>
              )}
            </FieldArray>
            <ModalActionFooter
              primary={
                <Submit disabled={false} size="large">
                  Save
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ReorderSidebarItemsModal;
